import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, Sparkles } from "lucide-react";
import { SITE } from "@/config/site";
import { Btn, SplitWords } from "./Motion";

const stack = [
  { src: "/projects/cleanmycar.webp", alt: "CleanMyCar website mockup", cls: "left-0 top-6 w-[62%]", rot: -6, bg: "#0055FF", depth: 18 },
  { src: "/projects/focus-fitness-whatsapp.webp", alt: "Focus Fitness WhatsApp AI chat", cls: "right-0 top-0 w-[52%]", rot: 6, bg: "#84CC16", depth: -26 },
  { src: "/projects/bangalore-real-estate.webp", alt: "Bangalore Real Estate Co. website", cls: "left-[14%] bottom-0 w-[66%]", rot: 2, bg: "#FF5500", depth: 34 },
];

const RotatingBadge = () => (
  <div className="absolute -left-4 bottom-10 z-20 hidden h-28 w-28 sm:block" aria-hidden="true">
    <svg viewBox="0 0 100 100" className="anim-spin h-full w-full">
      <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <circle cx="50" cy="50" r="49" fill="#FACC15" />
      <text fontSize="10.5" fontWeight="700" fill="#0F172A" letterSpacing="2"><textPath href="#circ">OURUMDIGITAL • WEB • AI • SOCIAL •</textPath></text>
    </svg>
    <Sparkles className="absolute left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 text-slate-900" />
  </div>
);

const Card = ({ s, i, mx, my }) => {
  const x = useTransform(mx, (v) => v * s.depth);
  const y = useTransform(my, (v) => v * s.depth);
  return (
    <motion.div
      initial={{ opacity: 0, y: 120, rotate: 0, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, rotate: s.rot, scale: 1 }}
      transition={{ delay: 0.55 + i * 0.16, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute ${s.cls}`}
    >
      <motion.div style={{ x, y }} whileHover={{ scale: 1.04, rotate: 0 }} transition={{ type: "spring", stiffness: 200, damping: 20 }}>
        <div className="anim-float rounded-[22px] p-2 shadow-[0_24px_60px_rgba(15,23,42,0.18)]" style={{ background: s.bg, animationDelay: `${-i * 2}s` }}>
          <img src={s.src} alt={s.alt} className="aspect-[4/3] w-full rounded-2xl bg-[#F4F1EA] object-cover object-top" />
        </div>
      </motion.div>
    </motion.div>
  );
};

const HeroVisual = ({ mx, my, py }) => (
  <motion.div style={{ y: py }} className="relative mx-auto h-[340px] w-full max-w-[560px] sm:h-[460px]">
    <div className="anim-blob absolute left-[10%] top-[8%] h-56 w-56 rounded-full bg-[#FF5566] opacity-60 blur-3xl" />
    <div className="anim-blob absolute bottom-[5%] right-[5%] h-64 w-64 rounded-full bg-[#8B5CF6] opacity-50 blur-3xl" style={{ animationDelay: "-6s" }} />
    {stack.map((s, i) => <Card key={s.src} s={s} i={i} mx={mx} my={my} />)}
    <motion.div initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 1.2, type: "spring", stiffness: 160, damping: 14 }}>
      <RotatingBadge />
    </motion.div>
  </motion.div>
);

const Headline = () => {
  const [a, b, c] = SITE.hero.headline;
  return (
    <h1 data-testid="hero-headline" className="font-display text-[2.7rem] font-extrabold leading-[1.02] tracking-[-0.035em] text-slate-900 sm:text-[4.2rem] lg:text-[3.6rem] xl:text-[4.25rem]">
      <SplitWords as="span" inView={false} text={a} className="block" />
      <motion.span
        initial={{ scale: 0.8, rotate: 0, opacity: 0 }}
        animate={{ scale: 1, rotate: -2, opacity: 1 }}
        transition={{ delay: 0.45, type: "spring", stiffness: 180, damping: 16 }}
        className="my-2 inline-block rounded-[0.35em] bg-[#0055FF] px-[0.28em] pb-[0.08em] text-white shadow-[0.12em_0.12em_0_#FACC15]"
      >
        {b}
      </motion.span>
      <SplitWords as="span" inView={false} delay={0.5} text={c} className="block" />
    </h1>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const mx = useSpring(useMotionValue(0), { stiffness: 60, damping: 18 });
  const my = useSpring(useMotionValue(0), { stiffness: 60, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const py = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const ringY = useTransform(scrollYProgress, [0, 1], [0, 260]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="grain relative overflow-hidden bg-[#FAF9F5] pb-24 pt-32 sm:pt-40">
      <motion.div style={{ y: ringY }} className="absolute -right-24 top-24 h-72 w-72 rounded-full border-[28px] border-[#FACC15]/40" aria-hidden="true" />
      <div className="container-x relative grid items-center gap-14 lg:grid-cols-[1.25fr_1fr]">
        <motion.div style={{ y: textY }}>
          <Headline />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }} data-testid="hero-description" className="mt-8 max-w-lg text-base leading-relaxed text-slate-700 sm:text-lg">{SITE.hero.description}</motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Btn href="#work" testid="hero-cta-explore" className="text-lg sm:px-9">Explore My Work <ArrowDownRight className="h-5 w-5 transition-transform group-hover:-rotate-45" /></Btn>
            <Btn href="#contact" testid="hero-cta-contact" variant="ghost">Let's Work Together <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" /></Btn>
          </motion.div>
        </motion.div>
        <HeroVisual mx={mx} my={my} py={py} />
      </div>
    </section>
  );
};

